import { useEffect, useState } from "react"
import LookupField from "../Theme/LookupField"
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import axiosInstance from "../../api/axiosInstance";
import Swal from 'sweetalert2';
import { saveFormData } from "./FormUtils";
export default function MedicalTemplateNotesForm({ saveTrigger, onSave, initialValues = null }) {
    const defaultFormData = {
        id: null,
        user_id: null,
        template_name: null,
        chief_complaint: null,
        history_of_present_illness: null,
        history_of_past_illness: null,
        examination_general: null,
        examination_systemic: null,
        examination_local: null,
        treatment_plan: null,
        advises: null,
        extra_notes: null,
        family_history: null,
        psychosocial: null,
        pain_assessment: null
    };
    const [form, setForm] = useState(defaultFormData);

    useEffect(() => {
        if (saveTrigger > 0) {
            save()
        }
    }, [saveTrigger]);

    const save = () => {
        if (!form.user_id || !form.template_name) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Template Name and User/Doctor are required fields.',
            });
            onSave(false);
            return;
        }
        const itemsOfInterest = Object.keys(defaultFormData)
            .filter((key) => key !== "id" && key !== "template_name" && key !== "user_id");
        const cleanedForm = {};
        for (const key of itemsOfInterest) {
            if (form[key]?.value) {
                cleanedForm[key] = form[key].value;
            }
        }
        const payload = {
            name: form.template_name.value,
            value: cleanedForm,
            user_id: form.user_id.value,
            id: form.id,
            type: "medical_notes"
        };
        saveFormData(payload, onSave);
    }

    const fetchInitialValues = async (values) => {
        const itemsOfInterest = Object.keys(defaultFormData)
            .filter((key) => key !== "id" && key !== "template_name" && key !== "user_id");

        if (values.length === 0) {
            setForm(defaultFormData)
            return;
        }

        await axiosInstance.post("medicalTemplates/fetchInitialValues/notes", values).then(response => {
            const data = response.data.data;
            itemsOfInterest.forEach((item) => {
                if (data[item]) {
                    values[item] = { value: values[item], label: data[item] };
                } else {
                    if (values[item]) {
                        values[item] = { value: values[item], label: "Not Found" };
                    }
                }
            })
            if (data.user_id) {
                values.user_id = { value: values.user_id, label: data.user_id }
            } else {
                if (values.user_id) {
                    values.user_id = { value: values.user_id, label: "Not Found" };
                }
            }
            setForm(values)
        }).catch(error => {
            console.error("Error fetching initial items:", error);
        });
    }

    const parseData = async () => {
        if (!initialValues) {
            return null;
        }
        let values = JSON.parse(JSON.stringify(initialValues));
        if (values.value) {
            try {
                values.value = JSON.parse(values.value);
            } catch (error) {
                values.value = {}
                console.error("Error parsing initial values:", error);
            }
        }
        values = { ...values, ...values.value }
        delete values.value;

        values.template_name = { value: values.name } || null;
        await fetchInitialValues(values);
    }
    useEffect(() => {
        parseData();
    }, [])

    const handleFormValueChange = (item, newValue) => {
        setForm((prev) => ({
            ...prev,
            [item]: newValue,
        }));
        const formCopy = {
            ...form,
            [item]: newValue,
        }
        const cleanedForm = {};
        for (const key in formCopy) {
            if (formCopy[key]?.value) {
                cleanedForm[key] = formCopy[key].value;
            }
        }
    }

    const chunkArray = (originalArray, chunkSize) => {
        const chunkedArray = [];
        for (let i = 0; i < originalArray.length; i += chunkSize) {
            const chunk = originalArray.slice(i, i + chunkSize);
            chunkedArray.push(chunk);
        }
        return chunkedArray
    }

    const fieldMap = {
        "chief_complaint": {
            "title": "Chief Complaint",
            "url": "searchMedicalField?table=chief_complaints&inclId=1&search="
        },
        "history_of_present_illness": {
            "title": "History of Present Illness",
            "url": "searchMedicalField?table=history_of_present_illness&inclId=1&search="
        },
        "history_of_past_illness": {
            "title": "History of Past Illness",
            "url": "searchMedicalField?table=history_of_past_illness&inclId=1&search="
        },
        "examination_general": {
            "title": "Examination [General]",
            "url": "searchMedicalField?table=examination_general&inclId=1&search="
        },
        "examination_systemic": {
            "title": "Examination [Systemic]",
            "url": "searchMedicalField?table=examination_systemic&inclId=1&search="
        },
        "examination_local": {
            "title": "Examination [Local]",
            "url": "searchMedicalField?table=examination_local&inclId=1&search="
        },
        "treatment_plan": {
            "title": "Treatment Plan",
            "url": "searchMedicalField?table=treatment_plan&inclId=1&search="
        },
        "advises": {
            "title": "Advises",
            "url": "searchMedicalField?table=advises&inclId=1&search="
        },
        "extra_notes": {
            "title": "Extra Notes",
            "url": "searchMedicalField?table=extra_notes&inclId=1&search="
        },
        "family_history": {
            "title": "Family History",
            "url": "searchMedicalField?table=family_history&inclId=1&search="
        },
    }
    const fieldKeys = Object.keys(fieldMap);
    const fieldTitles = chunkArray(fieldKeys, 2)

    return (<div>
        <div className="row">
            <div className="col-md-6">
                <div className="form-group">
                    <label>Template Name&nbsp;<span className="text-danger">*</span></label>
                    <input type="text" className="form-control p-2" value={form?.template_name?.value || ""} onInput={(e) => {
                        handleFormValueChange("template_name", { value: e.target.value })
                    }} />
                </div>
            </div>
            <div className="col-md-6">
                <div className="form-group">
                    <label>User/Doctor&nbsp;<span className="text-danger">*</span></label>
                    <LookupField initialValue={form.user_id} onChange={(newValue) => {
                        handleFormValueChange("user_id", newValue)
                    }} url="searchUsersList?search=" />
                </div>
            </div>
        </div>
        {fieldTitles.map((items, index) => (
            <div key={index} className="row mb-2">
                {items.map((item, index2) => (
                    <div key={`${index}.${index2}`} className="col-md-6">
                        <div className="form-group">
                            <label>{fieldMap[item].title}</label>
                            <LookupField initialValue={form[item]} onChange={(newValue) => {
                                handleFormValueChange(item, newValue)
                            }} url={fieldMap[item].url} />
                        </div>
                    </div>
                ))}
            </div>
        ))}
        <div className="row">
            <div className="col-md-6">
                <div className="form-group">
                    <label>Psychosocial</label>
                    <LookupField initialValue={form.psychosocial} onChange={(newValue) => {
                        handleFormValueChange("psychosocial", newValue)
                    }} url="searchMedicalField?table=psychosocial&inclId=1&search=" />
                </div>
            </div>
            <div className="col-md-6">
                <div className="form-group">
                    <label>Pain Assessment</label>
                    <Box>
                        <Slider
                            aria-label="Pain Assessment"
                            name="pain_assessment"
                            value={form?.pain_assessment?.value || 0}
                            min={0}
                            max={10}
                            step={1}
                            valueLabelDisplay="auto"
                            onChange={(event, newValue) => {
                                handleFormValueChange("pain_assessment", { value: newValue })
                            }}
                            marks={Array.from({ length: 10 }, (_, i) => ({
                                value: i + 1,
                                label: `${i + 1}`,
                            }))}
                            sx={{
                                color: "#4680ff", // active part color
                                height: 5, // slider track height
                                "& .MuiSlider-rail": {
                                    height: 5, // inactive track height
                                    borderRadius: 5,
                                },
                                "& .MuiSlider-track": {
                                    height: 5, // active track height
                                    borderRadius: 5,
                                },
                                "& .MuiSlider-thumb": {
                                    height: 20, // thumb size
                                    width: 20,
                                    backgroundColor: "#4680ff",
                                    border: "2px solid white",
                                },
                            }}
                        />
                    </Box>
                </div>
            </div>
        </div>
    </div>)
}